import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@invent/wl-ui-kit-next/ui';
import { Table2 } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { CondensedTip, RefreshButton, useAppBar, useCondensed, type IAppRefresh } from '@/design';

/** A read the header's *Refresh* asks again: the app bar's own shape. */
export type IHeaderRefresh = IAppRefresh;

/**
 * The right side of a work page's header (SR-485), the same on every work page and in one order:
 * the page's key number in one quiet line, *Open in Lists* where the page has a seeded view, and
 * the page's "?" last. In a compact bar (`condense.tsx`), *Open in Lists* is its icon, named as
 * before, and the key number is left to the page.
 *
 * ***Refresh* is the app bar's** (SR-514): the page hands it over (`IAppBar.setRefresh`) and the app
 * bar draws it left of its search, in one place on every page, and a compact bar there too. Only
 * the page's own header hands it over — the compact bar's copy of these actions does not — and
 * below 1024 px, where the app bar has no room for it, the page still draws it here. With no app
 * bar (another shell), the page draws it as before.
 */
export function WorkPageHeaderActions({
  summary,
  summaryData,
  lists,
  refresh,
  help
}: {
  summary: Nullable<string>;
  /** The summary's own hooks — its `role`, its `data-*` — as the page's specs know it. */
  summaryData?: Record<string, string>;
  /** The page's seeded view in Lists, and what doing it there means. */
  lists?: Nullable<{ to: string; tooltip?: string }>;
  refresh: IHeaderRefresh;
  help: React.ReactNode;
}) {
  const { condensed } = useCondensed();
  const setRefresh = useAppBar()?.setRefresh;
  useAppRefresh(condensed ? undefined : setRefresh, refresh);
  const open = lists && (
    <CondensedTip label="Open in Lists">
      <Button asChild variant="outline" size="sm" className={condensed ? 'size-8 p-0' : undefined}>
        <Link to={lists.to} aria-label={condensed ? 'Open in Lists' : undefined}>
          {condensed ? <Table2 className="size-4" aria-hidden /> : 'Open in Lists'}
        </Link>
      </Button>
    </CondensedTip>
  );
  return (
    <>
      {!condensed && (
        <span className="text-[0.8125rem] text-muted-foreground" {...summaryData}>
          {summary ?? ''}
        </span>
      )}
      {lists?.tooltip ? (
        <Tooltip>
          {/* A span of its own: the trigger's `data-slot` on the link would drop the button's styles. */}
          <TooltipTrigger asChild>
            <span className="inline-flex">{open}</span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">{lists.tooltip}</TooltipContent>
        </Tooltip>
      ) : (
        open
      )}
      {!(setRefresh && condensed) && (
        <span className={setRefresh ? 'inline-flex items-center gap-1 lg:hidden' : 'inline-flex items-center gap-1'}>
          <RefreshButton reading={refresh.reading} readAt={refresh.readAt} onClick={refresh.onClick} />
        </span>
      )}
      {help}
    </>
  );
}

/** Hands `refresh` to the app bar while mounted, again when its state changes — never per render,
 *  which would re-render the shell and the page in a loop — and takes it back on unmount. */
function useAppRefresh(
  setRefresh: ((refresh: Nullable<IHeaderRefresh>) => void) | undefined,
  refresh: IHeaderRefresh
) {
  const onClick = React.useRef(refresh.onClick);
  onClick.current = refresh.onClick;
  const readAt = refresh.readAt?.getTime() ?? null;
  React.useEffect(() => {
    setRefresh?.({
      reading: refresh.reading,
      readAt: readAt === null ? null : new Date(readAt),
      onClick: () => onClick.current()
    });
  }, [setRefresh, refresh.reading, readAt]);
  React.useEffect(() => (setRefresh ? () => setRefresh(null) : undefined), [setRefresh]);
}

/** The wall clock of a read's last answer: moves each time `answer` is a new one, never on a failure. */
export function useReadAt(answer: unknown): Nullable<Date> {
  const [readAt, setReadAt] = React.useState<Nullable<Date>>(null);
  React.useEffect(() => {
    if (answer != null) setReadAt(new Date());
  }, [answer]);
  return readAt;
}
